import { useState } from "react";
import { Check } from "lucide-react";
import { translations } from "../../i18n";
import { trackContact, trackInitiateCheckout } from "../../lib/pixel";
import { buildRegisterUrl } from "../../lib/tracking-url";

type Translation = (typeof translations)["de"];

function Pricing({
  isVisible,
  t,
  theme = "dark",
}: {
  isVisible: (id: string) => boolean;
  t: Translation["pricing"];
  theme?: "dark" | "light";
}) {
  // Default to yearly so users see the discounted monthly equivalent first —
  // matches the in-app /payments page and primes them for the higher-value plan.
  const [billing, setBilling] = useState<"monthly" | "yearly">("yearly");
  const plans = t.plans;
  const toggle = t.billingToggle;
  const isLight = theme === "light";

  return (
    <section
      id="pricing"
      data-animate
      className={`py-16 sm:py-24 md:py-32 transition-all duration-1000 ${
        isLight ? "bg-slate-50 border-t border-b border-slate-200" : ""
      } ${
        isVisible("pricing")
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2
          className={`text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-3 sm:mb-4 md:mb-6 ${
            isLight ? "text-slate-900" : ""
          }`}
        >
          {t.heading}
        </h2>
        <p
          className={`text-base sm:text-lg md:text-xl text-center mb-6 sm:mb-8 md:mb-10 ${
            isLight ? "text-slate-600" : "text-slate-400"
          }`}
        >
          {t.subheading}
        </p>

        {/* Billing-interval toggle */}
        <div className="flex justify-center mb-10 sm:mb-14 md:mb-16">
          <div
            role="tablist"
            aria-label="Billing interval"
            className={`inline-flex items-center gap-1 p-1 rounded-full border backdrop-blur ${
              isLight
                ? "border-slate-300 bg-white"
                : "border-slate-800 bg-slate-900/60"
            }`}
          >
            <button
              type="button"
              role="tab"
              aria-selected={billing === "monthly"}
              onClick={() => setBilling("monthly")}
              className={`px-4 sm:px-5 py-2 text-sm font-semibold rounded-full transition-colors ${
                billing === "monthly"
                  ? isLight
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-900"
                  : isLight
                  ? "text-slate-600 hover:text-slate-900"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              {toggle.monthly}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billing === "yearly"}
              onClick={() => setBilling("yearly")}
              className={`px-4 sm:px-5 py-2 text-sm font-semibold rounded-full transition-colors flex items-center gap-2 ${
                billing === "yearly"
                  ? isLight
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-900"
                  : isLight
                  ? "text-slate-600 hover:text-slate-900"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              {toggle.yearly}
              <span
                className={`text-[10px] font-bold leading-none rounded-full px-1.5 py-0.5 ${
                  billing === "yearly"
                    ? "bg-orange-500 text-white"
                    : "bg-orange-500/20 text-orange-600"
                }`}
              >
                {toggle.saveBadge}
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-5 sm:gap-6">
          {plans.map((plan) => {
            // For plans without a yearly tier (Free, Enterprise), always show
            // the static price/period. For paid plans, swap to the yearly
            // monthly-equivalent when the toggle is yearly. The period suffix
            // stays as `/month` (or `/Monat` in DE) because the displayed
            // price is the monthly equivalent either way.
            const showYearly = billing === "yearly" && !!plan.priceYearly;
            const displayedPrice = showYearly ? plan.priceYearly! : plan.price;
            const displayedPeriod = plan.period;
            return (
              <div
                key={plan.name}
                className={`relative h-full flex flex-col p-6 xl:p-5 rounded-2xl transition-all ${
                  plan.highlight
                    ? isLight
                      ? "bg-orange-50 border-2 border-orange-500 shadow-lg shadow-orange-500/10"
                      : "bg-orange-500/10 border-2 border-orange-500"
                    : isLight
                    ? "bg-white border border-slate-200 hover:border-slate-300 shadow-sm"
                    : "bg-slate-900/50 border border-slate-800 hover:border-slate-700"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-orange-500 text-white text-[11px] font-semibold tracking-wide uppercase whitespace-nowrap">
                    {t.popularLabel}
                  </span>
                )}
                <h3
                  className={`text-xl sm:text-2xl font-bold mb-2 ${
                    isLight ? "text-slate-900" : ""
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-1 sm:mb-2 min-h-[60px] sm:min-h-[68px] flex items-baseline gap-1 flex-wrap">
                  <span
                    className={`text-3xl sm:text-4xl xl:text-3xl 2xl:text-4xl font-bold leading-none ${
                      isLight ? "text-slate-900" : ""
                    }`}
                  >
                    {displayedPrice}
                  </span>
                  {displayedPeriod && (
                    <span
                      className={`text-sm ${
                        isLight ? "text-slate-500" : "text-slate-400"
                      }`}
                    >
                      {displayedPeriod}
                    </span>
                  )}
                </div>
                {/* Reserve fixed vertical space for the yearly note so cards stay aligned across plans */}
                <div className="min-h-[20px] mb-4 sm:mb-5">
                  {showYearly && plan.priceYearlyNote && (
                    <p
                      className={`text-xs ${
                        isLight ? "text-slate-500" : "text-slate-400"
                      }`}
                    >
                      {plan.priceYearlyNote}
                    </p>
                  )}
                </div>
                {plan.inheritedFrom && (
                  <p
                    className={`mb-3 text-sm font-medium italic ${
                      isLight ? "text-slate-700" : "text-slate-200"
                    }`}
                  >
                    {plan.inheritedFrom}
                  </p>
                )}
                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span
                        className={`text-sm leading-snug ${
                          isLight ? "text-slate-700" : "text-slate-300"
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href={
                    plan.contact
                      ? `mailto:neosearch@neobim.eu?cc=${encodeURIComponent(
                          "marko@neobim.eu,zuraiz@neobim.eu",
                        )}&subject=${encodeURIComponent(
                          `Enterprise inquiry — ${plan.name}`,
                        )}`
                      : buildRegisterUrl("https://app.neosearch.neobim.ai/start")
                  }
                  className="block mt-auto"
                  onClick={() =>
                    plan.contact
                      ? trackContact({ source: "pricing_enterprise" })
                      : trackInitiateCheckout({
                          source: "pricing",
                          plan: plan.name,
                          billing,
                        })
                  }
                >
                  <button
                    className={`w-full py-3 rounded-lg font-semibold transition-all group/btn ${
                      plan.highlight
                        ? "bg-orange-500 hover:bg-orange-600 text-white hover:shadow-lg hover:shadow-orange-500/50"
                        : isLight
                        ? "bg-slate-900 hover:bg-slate-800 text-white"
                        : "bg-slate-800 hover:bg-slate-700 text-white"
                    } active:scale-95`}
                  >
                    {plan.cta}
                    <span className="inline-block ml-2 group-hover/btn:translate-x-1 transition-transform">
                      →
                    </span>
                  </button>
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Pricing;
